import React, { useState } from 'react';
import axios from 'axios';

const CreateCodeRequest = ({ type, onClose, onSuccess, defaultGameName = '' }) => {
  const [formData, setFormData] = useState({
    gameName: defaultGameName,
    platform: 'steam',
    platformOther: '',
    gameUrl: '',
    description: '',
    codes: [''],
    cooldownDays: 0,
    emailNotificationsEnabled: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleCodeChange = (index, value) => {
    const newCodes = [...formData.codes];
    newCodes[index] = value;
    setFormData({ ...formData, codes: newCodes });
  };

  const addCodeField = () => {
    setFormData({ ...formData, codes: [...formData.codes, ''] });
  };

  const removeCodeField = (index) => {
    const newCodes = formData.codes.filter((_, i) => i !== index);
    setFormData({ ...formData, codes: newCodes });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.gameName.trim()) {
      setError('Please enter a game name');
      return;
    }

    if (formData.platform === 'other' && !formData.platformOther.trim()) {
      setError('Please specify the platform');
      return;
    }

    if (type === 'offer') {
      const validCodes = formData.codes.filter(c => c.trim());
      if (validCodes.length === 0) {
        setError('Please enter at least one code');
        return;
      }
    }

    try {
      setLoading(true);
      const token = localStorage.getItem('token');

      const payload = {
        type,
        gameName: formData.gameName.trim(),
        platform: formData.platform,
        platformOther: formData.platform === 'other' ? formData.platformOther.trim() : undefined,
        gameUrl: formData.gameUrl.trim() || undefined,
        description: formData.description.trim() || undefined,
        codes: type === 'offer' ? formData.codes.filter(c => c.trim()) : undefined,
        cooldownDays: type === 'offer' && formData.cooldownDays > 0 ? parseInt(formData.cooldownDays) : undefined,
        emailNotificationsEnabled: formData.emailNotificationsEnabled
      };

      await axios.post('/api/code-requests', payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onSuccess();
    } catch (err) {
      console.error('Error creating code request:', err);
      setError(err.response?.data?.message || 'Failed to create post');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content create-code-request-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{type === 'request' ? '📥 Request Game Code' : '📤 Offer Game Code'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        <form onSubmit={handleSubmit} className="create-form">
          {error && <div className="error-message">{error}</div>}

          {/* Game Name */}
          <div className="form-group">
            <label>
              Game Name <span className="required">*</span>
            </label>
            <input
              type="text"
              name="gameName"
              value={formData.gameName}
              onChange={handleChange}
              placeholder="e.g., Cyberpunk 2077"
              required
            />
            <small>The game banner will be fetched automatically from RAWG API</small>
          </div>

          {/* Platform */}
          <div className="form-group">
            <label>
              Platform <span className="required">*</span>
            </label>
            <select
              name="platform"
              value={formData.platform}
              onChange={handleChange}
              required
            >
              <option value="steam">🎮 Steam</option>
              <option value="epic">🏪 Epic Games</option>
              <option value="gog">🕹️ GOG</option>
              <option value="xbox">🎯 Xbox</option>
              <option value="playstation">🎲 PlayStation</option>
              <option value="nintendo">🎴 Nintendo</option>
              <option value="origin">🌟 Origin</option>
              <option value="uplay">🏰 Uplay</option>
              <option value="other">📦 Other</option>
            </select>
          </div>

          {/* Platform Other */}
          {formData.platform === 'other' && (
            <div className="form-group">
              <label>
                Specify Platform <span className="required">*</span>
              </label>
              <input
                type="text"
                name="platformOther"
                value={formData.platformOther}
                onChange={handleChange}
                placeholder="e.g., Battle.net, itch.io"
                required
              />
            </div>
          )}

          {/* Game URL */}
          <div className="form-group">
            <label>Game Store Link (Optional)</label>
            <input
              type="url"
              name="gameUrl"
              value={formData.gameUrl}
              onChange={handleChange}
              placeholder="https://store.steampowered.com/app/..."
            />
          </div>

          {/* Description */}
          <div className="form-group">
            <label>Description (Optional)</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              placeholder={
                type === 'request'
                  ? 'Add any additional details about your request...'
                  : 'Add any additional details about the codes...'
              }
              rows="3"
              maxLength="500"
            />
            <small>{formData.description.length}/500 characters</small>
          </div>

          {/* Codes (for offers only) */}
          {type === 'offer' && (
            <div className="form-group">
              <label>
                Game Codes <span className="required">*</span>
              </label>
              <small className="code-info">
                Enter one or more game codes. Each code will be given to a different person.
              </small>
              {formData.codes.map((code, index) => (
                <div key={index} className="code-input-group">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    placeholder={`Code ${index + 1}`}
                    className="code-input"
                  />
                  {formData.codes.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeCodeField(index)}
                      className="remove-code-btn"
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={addCodeField}
                className="add-code-btn"
              >
                + Add Another Code
              </button>
            </div>
          )}

          {/* Cooldown (for offers only) */}
          {type === 'offer' && (
            <div className="form-group">
              <label>Cooldown Period (Optional)</label>
              <small className="cooldown-info">
                Prevent users from claiming multiple codes from you too quickly. 
                If set, users must wait this many days after claiming a code before they can claim another from any of your offers.
              </small>
              <div className="cooldown-input-group">
                <input
                  type="number"
                  name="cooldownDays"
                  value={formData.cooldownDays}
                  onChange={handleChange}
                  min="0"
                  max="365"
                  placeholder="0"
                  className="cooldown-input"
                />
                <span className="cooldown-label">days</span>
              </div>
              {formData.cooldownDays > 0 && (
                <small className="cooldown-preview">
                  ⏱️ Users who claim a code must wait {formData.cooldownDays} day{formData.cooldownDays !== 1 ? 's' : ''} before claiming from you again
                </small>
              )}
            </div>
          )}

          {/* Email Notifications (for offers only) */}
          {type === 'offer' && (
            <div className="form-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  name="emailNotificationsEnabled"
                  checked={formData.emailNotificationsEnabled}
                  onChange={(e) => setFormData({ ...formData, emailNotificationsEnabled: e.target.checked })}
                />
                <span className="checkmark"></span>
                Send email notifications to claimants
              </label>
              <small className="email-info">
                When someone claims your code, they will receive an email with the code details in addition to the website message.
                Uncheck this if you prefer claimants only receive the website message.
              </small>
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary"
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={loading}
            >
              {loading ? 'Creating...' : type === 'request' ? 'Create Request' : 'Create Offer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateCodeRequest;
